
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import invoiceService from '../services/invoiceService';
import InvoicesTable from '../components/InvoicesTable';

const EMPTY_PAGINATION = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    from_date: '',
    to_date: '',
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '') {
          params[key] = value;
        }
      });

      const result = await invoiceService.getInvoices(params);

      setInvoices(
        Array.isArray(result.invoices) ? result.invoices : []
      );

      setPagination((previous) => ({
        ...previous,
        ...result.pagination,
      }));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Unable to load invoices.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    filters,
  ]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleSearch = (event) => {
    event.preventDefault();

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));

    setFilters((previous) => ({
      ...previous,
      search: searchInput.trim(),
    }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setSearchInput('');

    setPagination((previous) => ({
      ...previous,
      page: 1,
    }));

    setFilters({
      search: '',
      status: '',
      from_date: '',
      to_date: '',
    });
  };

  const handleDownload = async (invoice) => {
    try {
      setError('');

      await invoiceService.downloadInvoicePdf(
        invoice.invoice_number
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Unable to download invoice PDF.'
      );
    }
  };

  return (
    <section className="invoices-page">
      <header className="invoices-page-header">
        <div>
          <h1>Invoices</h1>
          <p>Manage customer invoices, billing, and payments.</p>
        </div>

        <Link
          to="/user/invoices/create"
          className="invoice-primary-button"
        >
          + Create Invoice
        </Link>
      </header>

      {error && (
        <div
          className="invoice-alert invoice-alert-error"
          role="alert"
        >
          {error}
        </div>
      )}

      <form className="invoices-toolbar" onSubmit={handleSearch}>
        <input
          type="search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search invoice number or customer..."
          aria-label="Search invoices"
        />

        <button
          type="submit"
          className="invoice-secondary-button"
        >
          Search
        </button>

        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          aria-label="Filter by invoice status"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="finalized">Finalized</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <label className="invoice-date-filter">
          <span>From date</span>
          <input
            type="date"
            name="from_date"
            value={filters.from_date}
            max={filters.to_date || undefined}
            onChange={handleFilterChange}
          />
        </label>

        <label className="invoice-date-filter">
          <span>To date</span>
          <input
            type="date"
            name="to_date"
            value={filters.to_date}
            min={filters.from_date || undefined}
            onChange={handleFilterChange}
          />
        </label>

        <button
          type="button"
          className="invoice-secondary-button"
          onClick={clearFilters}
        >
          Clear
        </button>
      </form>

      <div className="invoices-table-card">
        <div className="invoices-table-heading">
          <div>
            <h2>Invoice Register</h2>
            <p>View and download invoices issued by your firm.</p>
          </div>

          <span>{pagination.total} total</span>
        </div>

        <InvoicesTable
          invoices={invoices}
          loading={loading}
          onDownload={handleDownload}
        />

        <div className="invoices-pagination">
          <span>
            Page {pagination.page} of{' '}
            {Math.max(pagination.totalPages, 1)}
          </span>

          <div>
            <button
              type="button"
              disabled={loading || pagination.page <= 1}
              onClick={() =>
                setPagination((previous) => ({
                  ...previous,
                  page: previous.page - 1,
                }))
              }
            >
              Previous
            </button>

            <button
              type="button"
              disabled={
                loading ||
                pagination.page >= pagination.totalPages ||
                pagination.totalPages === 0
              }
              onClick={() =>
                setPagination((previous) => ({
                  ...previous,
                  page: previous.page + 1,
                }))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvoicesPage;
